/* eslint-disable @typescript-eslint/no-explicit-any */
import { apiClient } from '@store/index';
import { axiosErrorMiddleware } from './common';
import { QueryFunctionContext } from '@tanstack/react-query';

export enum TemplateType {
  REQUEST = 'request',
  CHATBOT = 'chatbot',
  MODULE = 'module',
  CALL_CONTROL = 'call_control',
  MESSAGING = 'messaging',
  USER_DEFINED = 'user_defined', // frontend only type
}

export interface Template {
  id: string;
  name: string;
  type: TemplateType;
  description: string;
  data: { [key: string]: any };
  org_id: 'telnyx' | string;
  data_prefixes: string;
  trigger_type: string;
  created_at: string;
  updated_at: string;
  updated_by: string;
}

export type TemplatesGetArgs = {
  id: string;
};

export type TemplatesCreateArgs = Omit<Template, 'id' | 'created_at' | 'updated_at' | 'updated_by' | 'org_id'>;

export type TemplatesUpdateArgs = {
  id: string;
  data: Partial<Omit<Template, 'created_at' | 'org_id'>> & { id: never };
};

export type TemplatesDeleteArgs = TemplatesGetArgs;

const API_ROUTE = 'templates';

const listTemplates = async (): Promise<Template[]> => {
  const response = await apiClient.get<Template[]>(`/${API_ROUTE}`);
  return response.data;
};

const getTemplate = async ({ queryKey }: QueryFunctionContext<[string, TemplatesGetArgs]>): Promise<Template> => {
  const [, { id }] = queryKey;
  const response = await apiClient.get<Template>(`/${API_ROUTE}/${id}`);
  return response.data;
};

const createTemplate = async (templateCreateData: TemplatesCreateArgs): Promise<Template> => {
  try {
    const response = await apiClient.post<Template>(`/${API_ROUTE}`, templateCreateData);
    return response.data;
  } catch (error) {
    throw new Error(axiosErrorMiddleware(error).error);
  }
};

const updateTemplate = async ({ id, data }: TemplatesUpdateArgs): Promise<Template> => {
  try {
    const response = await apiClient.put<Template>(`/${API_ROUTE}/${id}`, data);
    return response.data;
  } catch (error) {
    throw new Error(axiosErrorMiddleware(error).error);
  }
};

const deleteTemplate = async ({ id }: TemplatesDeleteArgs): Promise<{ message: string }> => {
  try {
    const response = await apiClient.delete(`/${API_ROUTE}/${id}`);
    return response.data;
  } catch (error) {
    throw new Error(axiosErrorMiddleware(error).error);
  }
};

export const templatesService = {
  list: listTemplates,
  get: getTemplate,
  create: createTemplate,
  update: updateTemplate,
  delete: deleteTemplate,
};
